import { useEffect } from "react";

/**
 * Per-page SEO head manager (dependency-free).
 * Updates <title>, meta description, canonical, robots, Open Graph, Twitter Cards
 * and an optional page-level JSON-LD block on route change.
 *
 * Note: static defaults (favicons, base OG, AutoDealer schema) live in public/index.html.
 */
function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  useEffect(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const pathname =
      path != null
        ? path
        : typeof window !== "undefined"
        ? window.location.pathname
        : "/";
    const url = `${origin}${pathname}`;
    const img = image
      ? image.startsWith("http")
        ? image
        : `${origin}${image}`
      : `${origin}/homepage.jpg`;

    if (title) document.title = title;
    if (description) upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "robots",
      noindex ? "noindex, follow" : "index, follow, max-image-preview:large",
    );
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, path, image, type, noindex, jsonLd]);

  return null;
}
